import { HttpApiEndpoint, HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { DatabaseError, DataIntegrityError } from "../auth/errors";
import { Authorization } from "../auth/middleware";
import {
  ConversationId,
  ConversationThreadId,
  DraftMessageId,
  MessageId,
} from "../ids/definitions";
import {
  ConversationListCursor,
  ConversationListItemResponse,
  ConversationResponse,
  UpdateConversationInput,
} from "../models/shopper-ai/conversation/conversation";
import {
  DraftResponse,
  SaveDraftInput,
  SendMessageInput,
  ThreadDraftsResponse,
} from "../models/shopper-ai/conversation/draft";
import {
  MessageCursor,
  MessageResponse,
} from "../models/shopper-ai/conversation/message";
import {
  ConversationThreadCursor,
  ConversationThreadResponse,
} from "../models/shopper-ai/conversation/thread";
import {
  CursorPaginatedResponse,
  CursorPaginationParams,
  CursorPaginationWithConversationQueryParams,
} from "../schema/pagination";

export const ConversationGroup = HttpApiGroup.make("conversation")
  .add(
    HttpApiEndpoint.get("getFilteredConversations", "/conversations")
      .middleware(Authorization)
      .setUrlParams(
        CursorPaginationWithConversationQueryParams(ConversationListCursor),
      )
      .addSuccess(
        CursorPaginatedResponse(
          ConversationListItemResponse,
          ConversationListCursor,
        ),
      )
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.get("getConversation", "/conversations/:id")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: ConversationId }))
      .addSuccess(ConversationListItemResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.get("getConversationThreads", "/conversations/:id/threads")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: ConversationId }))
      .setUrlParams(CursorPaginationParams(ConversationThreadCursor))
      .addSuccess(
        CursorPaginatedResponse(
          ConversationThreadResponse,
          ConversationThreadCursor,
        ),
      )
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.get(
      "getConversationMessages",
      "/conversations/:id/messages",
    )
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: ConversationId }))
      .setUrlParams(CursorPaginationParams(MessageCursor))
      .addSuccess(CursorPaginatedResponse(MessageResponse, MessageCursor))
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.patch("updateConversation", "/conversations/:id")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(UpdateConversationInput)
      .addSuccess(ConversationResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.post("saveDraft", "/conversations/:id/drafts")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(SaveDraftInput)
      .addSuccess(DraftResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.get("getDraft", "/conversations/:id/drafts")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: ConversationId }))
      .setUrlParams(
        Schema.Struct({
          threadId: ConversationThreadId,
          inReplyToMessageId: Schema.optional(MessageId),
        }),
      )
      .addSuccess(Schema.NullOr(DraftResponse))
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.get(
      "getThreadDrafts",
      "/conversations/thread/:id/drafts/all",
    )
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: ConversationThreadId }))
      .addSuccess(ThreadDraftsResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.del("deleteDraft", "/conversations/:id/drafts/:draftId")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: ConversationId, draftId: DraftMessageId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    HttpApiEndpoint.post("sendMessage", "/conversations/:id/messages")
      .middleware(Authorization)
      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(SendMessageInput)
      .addSuccess(Schema.Struct({ workflowId: Schema.String }))
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
